import { IColumn } from "cayo.ui";
import {
  AlertRule,
  ComparisonOperator,
  Job,
  OrderByDirection,
  SavedQuery,
  SoftLink,
} from "@cayo/shared/api/types/cayo-graph";
import { FilterItem } from "./common";

export enum ConditionType {
  Anr = "anr",
  Advanced = "advanced",
  Property = "property",
  TimeInterval = "timeInterval",
  Expression = "expression",
  MaxResults = "maxResults",
  ModernFilter = "modernFilter",
}

export enum ExpressionLogicType {
  LogicalExpression = "cayo.graph.logicalExpression",
  PropertyExpression = "cayo.graph.propertyExpression",
  OdataQueryExpression = "cayo.graph.rawExpression",
}

export enum ExpressionGroupCombinationType {
  AND = "and",
  OR = "or",
}

export enum ExpressionOperandOperator {
  equals = "equals",
  notEquals = "notEquals",
  contains = "contains",
  noContains = "noContains",
  present = "present",
  notPresent = "notPresent",
  greaterThan = "greaterThan",
  greaterThanOrEqual = "greaterThanOrEqual",
  lessThan = "lessThan",
  lessThanOrEqual = "lessThanOrEqual",
  startsWith = "startsWith",
}

export enum TimeIntervalType {
  LastHour = "cayo.graph.lastHourInterval",
  LastDay = "cayo.graph.lastDayInterval",
  LastWeek = "cayo.graph.lastWeekInterval",
  LastMonth = "cayo.graph.lastMonthInterval",
  LastDays = "cayo.graph.lastDaysInterval",
  Custom = "cayo.graph.customInterval",
  LastWeeks = "cayo.graph.lastWeeksInterval",
  LastMonths = "cayo.graph.lastMonthsInterval",
}

export enum PropertyDataType {
  StringCollection = "#Collection(String)",
  SoftLinkCollection = "#Collection(cayo.graph.softLink)",
}

export interface IQueryOrderBy {
  field: string;
  direction: OrderByDirection;
}

export interface IQuery {
  readonly id?: EntityId;
  readonly name?: string;
  readonly conditions: Readonly<Array<Condition>>;
  readonly isSystem: boolean;
  readonly orderBy?: Readonly<IQueryOrderBy>;
  readonly isTemp?: boolean;
  readonly isDefault?: boolean;
  readonly groupBy?: string;
  readonly effectiveFilter?: string;
  readonly alertRule?: AlertRule;
  readonly job?: Job;
  readonly jobResolved?: boolean;
}

export type SavedQueryItem = Pick<SavedQuery, "id" | "name" | "builtIn" | "default">;

export type PropertyObjectType = {
  id: string;
  type: string;
  key: string;
};

export type PropertyOperandType = {
  objectType: ExpressionLogicType.PropertyExpression;
  property: string;
  operator: ComparisonOperator;
  propertyObjectType: PropertyObjectType;
  enabled: boolean;
  value: string;
  columns?: IColumn[];
  valueLink?: { [key: string]: any };
  metadata?: { [key: string]: any };
};

export type OdataQueryOperandType = {
  objectType: ExpressionLogicType.OdataQueryExpression;
  enabled: boolean;
  filter: string;
  columns?: IColumn[];
};

export type LogicalOperandType = {
  objectType: ExpressionLogicType.LogicalExpression;
  operator: ExpressionGroupCombinationType;
  enabled: boolean;
  columns?: IColumn[];
  operands: Array<LogicalOperandType | PropertyOperandType | OdataQueryOperandType>;
};

export interface IExpressionCondition {
  kind: ConditionType.Expression;
  objectType: ExpressionLogicType;
  operator: ExpressionGroupCombinationType;
  operands: Array<LogicalOperandType | PropertyOperandType | OdataQueryOperandType>;
}

export interface IExpressionAdvancedPicker extends IExpressionCondition {
  operands: Array<
    LogicalOperandType & {
      name: string;
    }
  >;
}

export interface IAnrCondition {
  kind: ConditionType.Anr;
  value: string;
}

export interface IAdvancedCondition {
  kind: ConditionType.Advanced;
  value: string;
}

export interface IMaxResultsCondition {
  kind: ConditionType.MaxResults;
  value: string;
}

export interface IModernFilterCondition {
  kind: ConditionType.ModernFilter;
  value: Readonly<Array<FilterItem>>;
}

export type IPropCondition = IStringPropCondition | ISoftLinkPropCondition;

export interface IStringPropCondition {
  kind: ConditionType.Property;
  propName: string;
  dataType: PropertyDataType.StringCollection;
  value: Readonly<Array<string>>;
}

export interface ISoftLinkPropCondition {
  kind: ConditionType.Property;
  propName: string;
  dataType: PropertyDataType.SoftLinkCollection;
  value: Readonly<Array<SoftLink & { displayName?: string }>>;
}

export interface ITimeIntervalCondition {
  kind: ConditionType.TimeInterval;
  propName: string;
  value: TimeIntervalConditionValue;
}

export type Condition =
  | Readonly<IAnrCondition>
  | Readonly<IAdvancedCondition>
  | Readonly<IPropCondition>
  | Readonly<ITimeIntervalCondition>
  | Readonly<IExpressionCondition>
  | Readonly<IMaxResultsCondition>
  | Readonly<IModernFilterCondition>;

type FixedInterval = {
  objectType:
    | TimeIntervalType.LastHour
    | TimeIntervalType.LastDay
    | TimeIntervalType.LastWeek
    | TimeIntervalType.LastMonth;
};

export type LastDaysInterval = {
  objectType: TimeIntervalType.LastDays;
  number: Integer;
};

export type LastWeeksInterval = {
  objectType: TimeIntervalType.LastWeeks;
  number: Integer;
};

export type LastMonthsInterval = {
  objectType: TimeIntervalType.LastMonths;
  number: Integer;
};

export type CustomInterval = {
  objectType: TimeIntervalType.Custom;
  startDateTime: DateISO;
  endDateTime: DateISO;
};

export type TimeIntervalConditionValue =
  | Readonly<FixedInterval>
  | Readonly<LastDaysInterval>
  | Readonly<CustomInterval>
  | Readonly<LastWeeksInterval>
  | Readonly<LastMonthsInterval>;
